/* Inventory-rule cross-sell / upsell suggestions.
 *
 * Backed by a backend endpoint that uses supplier-tagged "frequently bought
 * together" pairs + same-store top sellers. No personalisation, no ML —
 * stays clear of the v1 non-goal "AI recommendations".
 *
 * Contexts:
 *   'order_checkout'        → suggestions during a regular order checkout.
 *                             Prefer the cart's branch first, then fill from
 *                             other branches if short.
 *   'subscription_modal'    → suggestions inside the Subscribe modal. Always
 *                             from the same store (passed via `branchId`).
 *   'home_routine_upsell'   → recommended subscribable products on Home
 *                             ("You might like"). Dedup against the
 *                             customer's existing subscriptions via `items`.
 */

import { api, mockOr } from '../lib/api.js';
import { D } from '../mock-data.js';

/**
 * @param {Object} args
 * @param {'order_checkout'|'subscription_modal'} args.context
 * @param {string} [args.branchId]   - required for subscription_modal; preferred branch for order_checkout
 * @param {Array<{productId?: string, id?: string}>} [args.items] - cart/seed items to exclude from suggestions
 * @param {number} [args.limit=3]
 * @returns {Promise<Array<{id:string, branchId:string, branchName:string, name:string, variantLabel:string, pricePaise:number, em:string, image:string|null}>>}
 */
export async function getRecommendations({ context, branchId, items = [], limit = 3 } = {}) {
  return mockOr(
    () => api.post('/recommendations', { context, branchId, items, limit }),
    () => mockSuggestions({ context, branchId, items, limit }),
  );
}

function mockSuggestions({ context, branchId, items, limit }) {
  const excluded = new Set((items || []).map((i) => i.productId || i.id).filter(Boolean));

  const fromBranch = (b) => b.products?.map((p) => ({
    id: p.id,
    branchId: b.id,
    branchName: b.name,
    name: p.name,
    variantLabel: p.variants?.[0]?.l ?? '',
    pricePaise: p.variants?.[0]?.p ?? 0,
    em: p.em,
    image: D.productImages?.[p.id] ?? null,
  })) ?? [];

  if (context === 'subscription_modal') {
    const b = D.branches.grocery.find((x) => x.id === branchId);
    if (!b) return [];
    return fromBranch(b).filter((s) => !excluded.has(s.id)).slice(0, limit);
  }

  if (context === 'home_routine_upsell') {
    // Pull subscribable products from any grocery branch, excluding what
    // the customer already has on their routine.
    const subscribableFromBranch = (b) => b.products?.filter((p) => p.subscribable).map((p) => ({
      id: p.id,
      branchId: b.id,
      branchName: b.name,
      branchRating: b.rating,
      name: p.name,
      variantLabel: p.variants?.[0]?.l ?? '',
      pricePaise: p.variants?.[0]?.p ?? 0,
      em: p.em,
      cat: p.cat,
      image: D.productImages?.[p.id] ?? null,
    })) ?? [];
    const pool = D.branches.grocery.flatMap(subscribableFromBranch)
      .filter((s) => !excluded.has(s.id));
    const seen = new Set();
    const out = [];
    for (const s of pool) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push(s);
      if (out.length >= limit) break;
    }
    return out;
  }

  // order_checkout: prefer cart's branch, then top up from other grocery branches.
  const preferred = branchId
    ? D.branches.grocery.find((x) => x.id === branchId)
    : null;
  const others = D.branches.grocery.filter((b) => b.id !== branchId);

  const pool = [
    ...(preferred ? fromBranch(preferred) : []),
    ...others.flatMap(fromBranch),
  ].filter((s) => !excluded.has(s.id));

  // Stable dedupe by id, preserve order.
  const seen = new Set();
  const out = [];
  for (const s of pool) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}
